import { mapGetters, mapActions } from 'vuex'

import Vue from 'vue'

import { store } from '_/vuex/store'

import { storage } from '_/services/storage'

import { router } from '_/router/router'

import { eventBus } from '_/helpers/eventBus'

import i18n from '_/i18n'

import sessionChecker from '_/helpers/sessionChecker'

import { pError } from '_/helpers/logger'

import * as adminSlots from '~/config/adminSlots'

import {
  Assets,
  Breadcrumb,
  EntryForm,
  HelpBlock,
  InfoPanel,
  Instructions,
  MainMenu,
  Reference,
  Sidebar,
  TitleBlock,
  UserMenu,
  SessionModal,
  SchedulePublishingModal,
} from '@/components'

const Search = Vue.component(
  'Search',
  require('_/components/Admin/Search/Search.vue').default
)

import { getEntryFields } from '_/components/Admin/Admin.fields'

let options = {
  rememberUpgrade: true,
  transports: ['websocket'],
  secure: true,
  rejectUnauthorized: false,
}
let socket
if (process.env.MIX_SOCKET_ENABLED === 'true') {
  socket = require('socket.io-client')(
    `${process.env.MIX_SOCKET_HOST}:${process.env.MIX_SOCKET_PORT}`,
    options
  )
}

export default {
  /**
   * Set the component data
   *
   * @return  {object}
   */
  data() {
    return {
      /**
       * Stores the entry fields configuration object
       *
       * @type  {Array}
       */
      fields: [],

      /**
       * An object sent to child components; used to signal that the fields need to be reset, with the ability
       * to limit the reset to only certain fields
       *
       * @type  {Object}
       */
      formFieldsReset: {
        /**
         * A list of field names to include in a reset action
         *
         * @type  {Array}
         */
        includeFields: [],

        /**
         * A parameter that is listened by the child components. Changed via moment().valueOf() method to always
         * set the fresh value.
         *
         * @type  {integer}
         */
        resetData: null,

        moduleTableName: this.$route.params.moduleTableName,
      },

      /**
       * Click counter
       *
       * @type  {integer}
       */
      clickCounter: 0,

      /**
       * Fields copy
       *
       * @type  {Array}
       */
      fieldsCopy: [],

      /**
       * First dialog fired state
       *
       * @type  {boolean}
       */
      isFirstDialogFired: true,

      /**
       * First dialog fired state
       *
       * @type  {boolean}
       */
      checkIsFormDirty: true,

      /**
       * Session checker interval id
       *
       * @type  {number}
       */
      sessionCheckerIntervalId: null,
    }
  },

  /**
   * Set the beforeRouteUpdate hook
   *
   * @return  {void}
   */
  beforeRouteUpdate(to, from, next) {
    if (!this.isFormDirty()) {
      return next()
    }

    if (!this.isFirstDialogFired) {
      this.isFirstDialogFired = true
      return
    }

    const answer = window.confirm(i18n.t('admin.changesMadeWarning'))

    if (!answer) {
      this.isFirstDialogFired = false
      return next(false)
    }

    next()
  },

  /**
   * Set the beforeRouteLeave hook
   *
   * @return  {void}
   */
  beforeRouteLeave(to, from, next) {
    if (!this.checkIsFormDirty) {
      return next()
    }

    if (!this.isFormDirty()) {
      return next()
    }

    const answer = window.confirm(i18n.t('admin.changesMadeWarning'))

    if (!answer) {
      return next(false)
    }

    next()
  },

  /**
   * Set the beforeMount hook
   *
   * @return  {void}
   */
  beforeMount() {
    window.addEventListener('beforeunload', this.preventNav)
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      admin: 'admin/admin',
      advancedSearch: 'advancedSearch/advancedSearch',
      photonModule: 'photonModule/photonModule',
      ui: 'ui/ui',
    }),
  },

  /**
   * Set the methods
   *
   * @type  {Object}
   */
  methods: {
    ...mapActions('admin', ['setAssetManagerSearchFilterObject']),

    ...mapActions('ui', [
      // TODO: Check if UI can work without animateWrapper action. If it can delete it project-wide.
      'animateWrapper',
      'getFilteredLocalesFromStorage',
      'setBodyClass',
      'triggerCreateMode',
    ]),

    ...mapActions('sidebar', ['setSidebarType']),

    /**
     * Triggers the create mode
     *
     * @return  {void}
     */
    createNewEntry() {
      this.triggerCreateMode()
    },

    /**
     * Sets the formFieldsReset data property in order to force refresh of the EntryForm component
     *
     * @param   {object}  options.includeFields The names of field components that need to be included in reset
     * @return  {void}
     */
    resetFormFields({ includeFields = null } = {}) {
      this.formFieldsReset.resetData = moment().valueOf()

      this.formFieldsReset.includeFields = []

      if (includeFields) {
        this.formFieldsReset.includeFields = includeFields
      }

      eventBus.$emit('formFieldsReset', this.formFieldsReset)
    },

    /**
     * Prevents the navigation upon reload callback function
     *
     * @param   {event}  event
     * @return  {void}
     */
    preventNav(event) {
      if (this.isFormDirty()) {
        event.preventDefault()

        event.returnValue = ''
      }
    },

    /**
     * Navigates to a selected entry
     *
     * @param   {object}
     * @return  {void}
     */
    navigateToEntry({ entry, openInNewTab }) {
      if (openInNewTab) {
        let routeData = this.$router.resolve(
          `/admin/${this.$route.params.moduleTableName}/${entry.system.id}`
        )
        window.open(routeData.href, '_blank')
        return
      }

      this.$router.push(
        `/admin/${this.$route.params.moduleTableName}/${entry.system.id}`
      )
    },

    /**
     * Catches the user interaction with entry form
     *
     * @return  {void}
     */
    onFirstInteract() {
      if (this.clickCounter === 0) {
        this.fieldsCopy = this.fields.map(field => {
          return { ...field }
        })

        this.checkIsFormDirty = true

        this.clickCounter++
      }
    },

    /**
     * Checks if form data is dirty
     *
     * @return  {Boolean}
     */
    isFormDirty() {
      let isDirty = false

      for (let i = 0; i < this.fieldsCopy.length; i++) {
        const field = this.fields[i]
        const fieldCopy = this.fieldsCopy[i]
        if (field.value !== fieldCopy.value) {
          isDirty = true
          break
        }
      }

      return isDirty
    },
  },

  /**
   * Define components
   *
   * @type  {Object}
   */
  components: {
    SchedulePublishingModal,
    ...adminSlots,
    Assets,
    Breadcrumb,
    EntryForm,
    HelpBlock,
    InfoPanel,
    Instructions,
    MainMenu,
    Search,
    Sidebar,
    TitleBlock,
    UserMenu,
    SessionModal,
  },

  /**
   * Set the beforeRouteEnter hook
   *
   * @return  {void}
   */
  beforeRouteEnter: function(to, from, next) {
    const moduleTableName = to.params.moduleTableName

    const shouldFetchSearch = to.params.moduleEntryId === 'search'

    const moduleEntryId = !isNaN(to.params.moduleEntryId)
      ? to.params.moduleEntryId
      : null

    if (!moduleTableName) {
      return router.push('/error/resource-not-found')
    }

    store
      .dispatch('admin/adminBootstrap', {
        moduleTableName,
        moduleEntryId,
        shouldFetchSearch,
      })
      .then(() => {
        next()
      })
      .catch(error => {
        pError('admin/adminBootstrap action failed with an error', error)

        router.push('/error/resource-not-found')
      })
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted: function() {
    this.getFilteredLocalesFromStorage()

    eventBus.$on('deleteEntryConfirmed', () => {
      this.checkIsFormDirty = false
    })
    eventBus.$on('entryCreated', () => {
      this.checkIsFormDirty = false
    })
    eventBus.$on('entryUpdated', moduleName => {
      if (moduleName === 'admin') {
        this.checkIsFormDirty = false
        this.clickCounter = 0
        this.fieldsCopy = []
      }
    })
    this.fields = getEntryFields(this)

    this.$nextTick(function() {
      this.setBodyClass('admin')
      this.setSidebarType('AdminSidebar')
      this.animateWrapper()
      this.sessionCheckerIntervalId = sessionChecker.setCheckerInterval()
    })

    const top = $('.mobile-control-buttons').offset().top

    $(window).scroll(function() {
      const y = $(this).scrollTop()
      if (y >= top) {
        $('.mobile-control-buttons').addClass('fixed')
      } else {
        $('.mobile-control-buttons').removeClass('fixed')
      }
      $('.sticky-scroll-box').width(
        $('.sticky-scroll-box')
          .parent()
          .width()
      )
    })
  },

  /**
   * Set the beforeDestroy hook
   *
   * @return  {void}
   */
  beforeDestroy: function() {
    if (this.validator) {
      destroyValidator(this.validator)
    }

    window.removeEventListener('beforeunload', this.preventNav)
    this.setSidebarType(null)
    this.setAssetManagerSearchFilterObject({ value: {} })
    clearInterval(this.sessionCheckerIntervalId)
    this.sessionCheckerIntervalId = null
  },

  /**
   * Define watched properties
   *
   * @type  {Object}
   */
  watch: {
    /**
     * Watching the route change
     *
     * @return  {void}
     */
    $route(newEntry, oldEntry) {
      const moduleTableName = newEntry.params.moduleTableName
      const shouldFetchSearch = newEntry.params.moduleEntryId === 'search'
      const moduleEntryId = !isNaN(newEntry.params.moduleEntryId)
        ? newEntry.params.moduleEntryId
        : null
      if (!moduleTableName) {
        return router.push('/error/resource-not-found')
      }

      if (newEntry.params.moduleTableName !== oldEntry.params.moduleTableName) {
        this.setAssetManagerSearchFilterObject({ value: {} })
      }

      store
        .dispatch('admin/adminBootstrap', {
          moduleTableName,
          moduleEntryId,
          shouldFetchSearch,
        })
        .then(() => {
          this.fields = getEntryFields(this)
          this.resetFormFields()
        })
        .catch(error => {
          pError('admin/adminBootstrap action failed with an error', error)
          router.push('/error/resource-not-found')
        })

      this.clickCounter = 0
      this.fieldsCopy = []
    },

    'admin.entryUpdated'(newEntry, oldEntry) {
      this.fields = getEntryFields(this)
      this.resetFormFields()
    },

    'admin.editedEntry': {
      deep: true,
      handler(newEntry, oldEntry) {
        if (
          process.env.MIX_SOCKET_ENABLED === 'true' &&
          this.admin.selectedModule &&
          this.admin.selectedModule.content_preview !== null &&
          newEntry.system.id
        ) {
          this.fields = getEntryFields(this)
          socket.on('connection', socket => {
            socket.join(
              `${process.env.MIX_APP_DOMAIN}-livePreview-${this.admin.selectedModule.table_name}-${newEntry.system.id}`
            )
          })
          $.each(newEntry.fields, function(key, value) {
            socket.emit(key, value)
          })
        }
      },
    },
  },
}
